/**
 * WeatherCard (spec 015 · T061).
 *
 * Compact temperature + condition + location chip. Composed inside
 * LauncherHero's `.launcher-hero__card` (shares its surface/border/shadow —
 * this component renders no card chrome of its own) so the mockup's single
 * clock+weather card comes through as one surface. Also usable standalone
 * (see WeatherCard.test.tsx) since it carries no positioning assumptions.
 *
 * Null weather → subtle "Weather unavailable" state, not an error.
 */
import {
  Cloud,
  CloudFog,
  CloudRain,
  CloudSun,
  MapPin,
  Moon,
  Snowflake,
  Sun,
  Sunset,
} from 'lucide-react';

import type {
  ConditionCode,
  LauncherLocation,
  LauncherWeather,
} from '@/services/launcher';

interface WeatherCardProps {
  weather: LauncherWeather | null;
}

interface IconDef { Icon: typeof Sun; testid: string }

const ICON_MAP: Record<ConditionCode, IconDef> = {
  clear: { Icon: Sun, testid: 'weather-icon-clear' },
  cloudy: { Icon: Cloud, testid: 'weather-icon-cloudy' },
  rain: { Icon: CloudRain, testid: 'weather-icon-rain' },
  snow: { Icon: Snowflake, testid: 'weather-icon-snow' },
  fog: { Icon: CloudFog, testid: 'weather-icon-fog' },
  sunset: { Icon: Sunset, testid: 'weather-icon-sunset' },
  night: { Icon: Moon, testid: 'weather-icon-night' },
};

function LocationChip({ location }: { location: LauncherLocation }) {
  // "from <source> · <label> <zip?>" — source is the styled anchor word
  const label = location.label.toLowerCase();
  const zip = location.zip ? ` ${location.zip}` : '';
  const tail = location.source === 'localtea' ? ` · ${label}${zip}` : '';

  return (
    <div className="weather-card__chip" data-testid="weather-location-chip">
      <MapPin aria-hidden="true" />
      <span>
        from <strong>{location.source}</strong>
        {tail}
      </span>
    </div>
  );
}

export default function WeatherCard({ weather }: WeatherCardProps) {
  if (!weather) {
    return (
      <div className="weather-card weather-card--empty" role="status">
        <p className="weather-card__empty">Weather unavailable.</p>
        <p className="weather-card__empty-sub">we'll try again shortly</p>
      </div>
    );
  }

  const iconDef = ICON_MAP[weather.condition_code] ?? {
    Icon: CloudSun,
    testid: 'weather-icon-other',
  };
  const { Icon, testid } = iconDef;

  return (
    <div className="weather-card">
      <div className="weather-card__divider" />
      <div className="weather-card__row">
        <div className="weather-card__icon" data-testid={testid}>
          <Icon aria-hidden="true" />
        </div>
        <div className="weather-card__body">
          <div className="weather-card__temp">
            <span className="weather-card__temp-value">{Math.round(weather.temp_f)}</span>
            <span className="weather-card__temp-unit">°F</span>
            <span className="weather-card__temp-sep"> · </span>
            <span className="weather-card__cond-label">{weather.condition_label}</span>
          </div>
          {weather.sub_line ? (
            <div className="weather-card__sub">{weather.sub_line}</div>
          ) : null}
        </div>
      </div>
      <LocationChip location={weather.location} />
    </div>
  );
}
